import clsx from 'clsx';
import * as React from 'react';

import { colors } from '@gitbook/react-uikit';
import {
    Actions,
    Button,
    ColorField,
    ColorPicker,
    ColorPickerControls,
    type ColorPickerProps,
    ColorSwatch,
    ColorSwatchPicker,
    ColorSwatchPickerItem,
    Description,
    EyeDropperButton,
    FieldGroup,
    Group,
    Label,
    Popover,
    PopoverRoot,
    PopoverTrigger,
    usePopoverTriggerState,
} from '@gitbook/spine-react';

import type { SitesCustomizationPanelProps } from '../types';
import { useRecentColors } from './useRecentColors';

type ColorPickerFormProps = SitesCustomizationPanelProps & {
    label: string;
    description?: string;
    currentColor?: string;
    setColor: (newColor: string) => void;
    size?: 'small' | 'medium';
    orientation?: 'horizontal' | 'vertical';
    className?: string;
};

/**
 * Settings to edit a color in the space or collection.
 */
export function ColorPickerForm(props: ColorPickerFormProps): React.ReactElement {
    const {
        label,
        description,
        currentColor,
        setColor,
        size = 'medium',
        orientation = 'horizontal',
        className,
    } = props;
    const value = currentColor || colors.PRIMARY;
    const { addRecentColor } = useRecentColors();
    const actionOnCloseRef = React.useRef<'commit' | 'reset' | null>(null);

    const commit = () => {
        setColor(localColor);
        addRecentColor(localColor);
    };

    const reset = () => setLocalColor(value);

    const trigger = usePopoverTriggerState({
        onOpenChange: (isOpen) => {
            if (!isOpen) {
                switch (actionOnCloseRef.current) {
                    case 'reset':
                        reset();
                        break;
                    default:
                        commit();
                        break;
                }
            } else {
                actionOnCloseRef.current = null;
            }
        },
    });

    const [localColor, setLocalColor] = React.useState(value);
    React.useEffect(() => {
        setLocalColor(value);
    }, [value]);

    return (
        <FieldGroup orientation={orientation}>
            <Group>
                {size === 'small' ? <Label headless>{label}</Label> : <Label>{label}</Label>}
                {description ? <Description>{description}</Description> : null}
            </Group>
            <PopoverRoot {...trigger.rootProps}>
                <PopoverTrigger>
                    <Button
                        kind="blank"
                        label={value}
                        leadingIcon={<ColorSwatch color={value} />}
                        size={size}
                        className={clsx('justify-between', className)}
                    />
                </PopoverTrigger>

                <Popover className="p-2">
                    <CustomColorPicker
                        value={localColor}
                        onChange={(color) => {
                            const value = color.toString('hex');
                            setLocalColor(value);
                        }}
                        onApply={() => {
                            actionOnCloseRef.current = 'commit';
                            trigger.close();
                        }}
                        onCancel={() => {
                            actionOnCloseRef.current = 'reset';
                            trigger.close();
                        }}
                    />
                </Popover>
            </PopoverRoot>
        </FieldGroup>
    );
}

function CustomColorPicker(
    props: Pick<ColorPickerProps, 'value' | 'onChange'> & {
        onCancel: () => void;
        onApply: () => void;
    }
) {
    const { onCancel, onApply } = props;
    const { recentColors } = useRecentColors();
    return (
        <ColorPicker {...props}>
            <div className="flex w-fit flex-col gap-4">
                <ColorPickerControls />
                {recentColors.length > 0 && (
                    <ColorSwatchPicker>
                        {recentColors.map((color) => (
                            <ColorSwatchPickerItem color={color} key={color}>
                                <ColorSwatch className="size-8" />
                            </ColorSwatchPickerItem>
                        ))}
                    </ColorSwatchPicker>
                )}
                <div className="flex gap-2">
                    <ColorField
                        className="flex-1"
                        autoFocus
                        aria-label="Custom color input field"
                        onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                                onApply();
                            }
                        }}
                    />
                    <EyeDropperButton />
                </div>

                <Actions>
                    <Button kind="secondary" onPress={onCancel} label="Cancel" />
                    <Button kind="primary" onPress={onApply} label="Apply" />
                </Actions>
            </div>
        </ColorPicker>
    );
}
